import { type Graphics, Rectangle } from "pixi.js";
import { memo, useCallback, useMemo } from "react";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import { ACTIVE_INTERACTION_COLOR } from "../interaction";
import type {
    FlylineMapObject,
    LayerId,
    MapItemHandler,
    Point2D,
} from "../types";

const ARROW_SIZE_WORLD = 0.2;
const ARROW_SPACING_WORLD = 10;
const LINE_WIDTH_WORLD = 0.1;
const ENDPOINT_RADIUS_WORLD = 0.1;
interface FlylineObjectProps {
    item: FlylineMapObject;
    layerId: LayerId;
    selected: boolean;
    onItemClick?: MapItemHandler<FlylineMapObject>;
    onItemHover?: MapItemHandler<FlylineMapObject>;
    onItemLeave?: MapItemHandler<FlylineMapObject>;
}

type Triangle = readonly [Point2D, Point2D, Point2D];

function getArrowCount(length: number): number {
    if (length === 0) return 0;
    if (length < ARROW_SPACING_WORLD) return 1;
    return Math.floor(length / ARROW_SPACING_WORLD);
}

function getArrowTriangles(item: FlylineMapObject): Triangle[] {
    const dx = item.endX - item.startX;
    const dy = item.endY - item.startY;
    const length = Math.hypot(dx, dy);
    const count = getArrowCount(length);

    if (count === 0) return [];

    const directionX = dx / length;
    const directionY = dy / length;
    const perpendicularX = -directionY;
    const perpendicularY = directionX;

    return Array.from({ length: count }, (_, index) => {
        const fraction = (index + 1) / (count + 1);
        const centerX = item.startX + dx * fraction;
        const centerY = item.startY + dy * fraction;
        const baseCenterX = centerX - ARROW_SIZE_WORLD * directionX;
        const baseCenterY = centerY - ARROW_SIZE_WORLD * directionY;

        return [
            {
                x: centerX + ARROW_SIZE_WORLD * directionX,
                y: centerY + ARROW_SIZE_WORLD * directionY,
            },
            {
                x: baseCenterX + ARROW_SIZE_WORLD * perpendicularX,
                y: baseCenterY + ARROW_SIZE_WORLD * perpendicularY,
            },
            {
                x: baseCenterX - ARROW_SIZE_WORLD * perpendicularX,
                y: baseCenterY - ARROW_SIZE_WORLD * perpendicularY,
            },
        ] as const;
    });
}

function useFlylineGeometry(item: FlylineMapObject) {
    const { coordinates } = useScene();

    return useMemo(() => {
        const start = coordinates.worldToScene({
            x: item.startX,
            y: item.startY,
        });
        const end = coordinates.worldToScene({ x: item.endX, y: item.endY });
        const triangles = getArrowTriangles(item).map(
            ([tip, baseLeft, baseRight]): Triangle => [
                coordinates.worldToScene(tip),
                coordinates.worldToScene(baseLeft),
                coordinates.worldToScene(baseRight),
            ],
        );
        const padding = ARROW_SIZE_WORLD * Math.SQRT2;
        const cullWorldRect = {
            x: Math.min(item.startX, item.endX) - padding,
            y: Math.min(item.startY, item.endY) - padding,
            width: Math.abs(item.endX - item.startX) + 2 * padding,
            height: Math.abs(item.endY - item.startY) + 2 * padding,
        };
        const cullSceneRect = coordinates.worldRectToScene(cullWorldRect);
        const bounds = new Rectangle(
            cullSceneRect.x,
            cullSceneRect.y,
            cullSceneRect.width,
            cullSceneRect.height,
        );

        return {
            start,
            end,
            triangles,
            bounds,
            lineWidth: coordinates.worldLengthToScene(LINE_WIDTH_WORLD),
            endpointRadius: coordinates.worldLengthToScene(
                ENDPOINT_RADIUS_WORLD,
            ),
        };
    }, [coordinates, item]);
}

function drawTriangles(
    graphics: Graphics,
    triangles: readonly Triangle[],
    color: number,
    alpha: number,
) {
    graphics.clear();

    for (const [tip, baseLeft, baseRight] of triangles) {
        graphics.poly([
            tip.x,
            tip.y,
            baseLeft.x,
            baseLeft.y,
            baseRight.x,
            baseRight.y,
        ]);
        graphics.fill({ color, alpha });
    }
}

const FlylineObject = ({
    item,
    layerId,
    selected,
    onItemClick,
    onItemHover,
    onItemLeave,
}: FlylineObjectProps) => {
    const { start, end, triangles, bounds, lineWidth, endpointRadius } =
        useFlylineGeometry(item);
    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });
    const active = interaction.hovered || selected;
    const drawLine = useCallback(
        (graphics: Graphics) => {
            const color = active ? ACTIVE_INTERACTION_COLOR : item.color;

            graphics.clear();
            graphics.moveTo(start.x, start.y);
            graphics.lineTo(end.x, end.y);
            graphics.stroke({ color, alpha: item.alpha, width: lineWidth });
            graphics.circle(end.x, end.y, endpointRadius);
            graphics.fill({ color, alpha: item.alpha });
        },
        [active, end, endpointRadius, item.alpha, item.color, lineWidth, start],
    );
    const drawArrows = useCallback(
        (graphics: Graphics) => {
            drawTriangles(
                graphics,
                triangles,
                active ? ACTIVE_INTERACTION_COLOR : item.color,
                item.alpha,
            );
        },
        [active, item.alpha, item.color, triangles],
    );

    return (
        <pixiContainer
            label="flyline-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={bounds}
        >
            <pixiGraphics eventMode="none" draw={drawLine} />
            <pixiGraphics
                eventMode={
                    triangles.length === 0 ? "none" : interaction.eventMode
                }
                cursor={triangles.length === 0 ? undefined : interaction.cursor}
                onPointerOver={interaction.onPointerOver}
                onPointerOut={interaction.onPointerOut}
                onPointerTap={interaction.onPointerTap}
                draw={drawArrows}
            />
        </pixiContainer>
    );
};

export default memo(FlylineObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.selected === next.selected &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
